import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  where,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { firestore, auth } from "../../../firebase";
import { onAuthStateChanged } from "firebase/auth";

const FACILITY_SCOPED_ROLES = ["seller", "facility_operator", "technician", "Technician"];

const getRoleLabel = (role) => {
  switch (role) {
    case "facility_operator":
      return "Seller";
    case "technician":
    case "Technician":
      return "Technician";
    case "admin":
      return "Admin";
    case "customer":
      return "Customer";
    default:
      return role;
  }
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userData, setUserData] = useState({
    email: "",
    displayName: "",
    phoneNumber: "",
    role: "customer",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [filterRole, setFilterRole] = useState("all");
  const [currentAdmin, setCurrentAdmin] = useState(null);

  const canManageFacilityScopedRoles = currentAdmin?.role === "superadmin";

  // ✅ STEP 1: Get or create current admin profile
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDocRef = doc(firestore, "UserProfileMaster", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            setCurrentAdmin({
              uid: user.uid,
              email: user.email,
              tenantId: userData.tenantId,
              role: userData.role,
            });
          } else {
            // ✅ Create default profile if missing
            const defaultProfile = {
              email: user.email,
              displayName: user.displayName || "",
              phoneNumber: user.phoneNumber || "",
              role: "admin",
              tenantId: `tenant-${user.uid.substring(0, 6)}`,
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            await setDoc(userDocRef, defaultProfile);
            setCurrentAdmin({
              uid: user.uid,
              email: user.email,
              tenantId: defaultProfile.tenantId,
              role: defaultProfile.role,
            });
          }
        } catch (error) {
          console.error("Error fetching/creating admin profile:", error);
        }
      } else {
        setCurrentAdmin(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // ✅ STEP 2: Fetch users (tenant-wise or all if superadmin)
  useEffect(() => {
    if (!currentAdmin) return;

    let q;
    if (currentAdmin.role === "superadmin") {
      q = query(collection(firestore, "UserProfileMaster"));
    } else if (currentAdmin.tenantId) {
      q = query(
        collection(firestore, "UserProfileMaster"),
        where("tenantId", "==", currentAdmin.tenantId)
      );
    } else {
      setUsers([]);
      return;
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const userList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(userList);
    });

    return () => unsubscribe();
  }, [currentAdmin]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserData({
        email: user.email,
        displayName: user.displayName || "",
        phoneNumber: user.phoneNumber || "",
        role: user.role || "customer",
      });
    } else {
      setEditingUser(null);
      setUserData({
        email: "",
        displayName: "",
        phoneNumber: "",
        role: "customer",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setUserData({
      email: "",
      displayName: "",
      phoneNumber: "",
      role: "customer",
    });
  };

  // ✅ Save or Update user
  const handleSaveUser = async () => {
    try {
      if (!currentAdmin) return;

      if (!canManageFacilityScopedRoles && FACILITY_SCOPED_ROLES.includes(userData.role)) {
        setSnackbar({
          open: true,
          message: "Seller and Technician roles must be created by Facility Admin",
          severity: "error",
        });
        return;
      }

      if (editingUser) {
        await updateDoc(doc(firestore, "UserProfileMaster", editingUser.id), {
          ...userData,
          tenantId: currentAdmin?.tenantId || "N/A",
          updatedAt: new Date(),
        });
        setSnackbar({
          open: true,
          message: "User updated successfully",
          severity: "success",
        });
      } else {
        const newUserData = {
          ...userData,
          tenantId: currentAdmin?.tenantId || "N/A",
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        await addDoc(collection(firestore, "UserProfileMaster"), newUserData);
        setSnackbar({
          open: true,
          message: "User added successfully",
          severity: "success",
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving user:", error);
      setSnackbar({
        open: true,
        message: "Error saving user",
        severity: "error",
      });
    }
  };

  // ✅ Delete user
  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteDoc(doc(firestore, "UserProfileMaster", userId));
        setSnackbar({
          open: true,
          message: "User deleted successfully",
          severity: "success",
        });
      } catch (error) {
        console.error("Error deleting user:", error);
        setSnackbar({
          open: true,
          message: "Error deleting user",
          severity: "error",
        });
      }
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "warning";
      case "seller":
      case "facility_operator":
        return "info";
      case "technician":
      case "Technician":
        return "success";
      case "customer":
        return "default";
      default:
        return "default";
    }
  };

  const visibleUsers = canManageFacilityScopedRoles
    ? users
    : users.filter((user) => !FACILITY_SCOPED_ROLES.includes(user.role));

  const filteredUsers =
    filterRole === "all"
      ? visibleUsers
      : visibleUsers.filter((user) => user.role === filterRole);

  return (
    <Container>
      <Box
        sx={{
          my: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={!currentAdmin}
        >
          Add Role
        </Button>
      </Box>

      {/* {currentAdmin && currentAdmin.role !== "superadmin" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing only users under your tenant:{" "}
          <strong>{currentAdmin.tenantId || "(not set)"}</strong>
        </Alert>
      )} */}

      <Box sx={{ my: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Role</InputLabel>
          <Select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            label="Filter by Role"
          >
            <MenuItem value="all">All Users</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            {canManageFacilityScopedRoles && <MenuItem value="seller">Seller</MenuItem>}
            {canManageFacilityScopedRoles && <MenuItem value="technician">Technician</MenuItem>}
            {canManageFacilityScopedRoles && <MenuItem value="Technician">Technician</MenuItem>}
            <MenuItem value="customer">Customer</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No users found for this tenant.
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.displayName}</TableCell>
                  <TableCell>{user.phoneNumber}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(user.role)}
                      color={getRoleColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {user.createdAt
                      ? new Date(
                          user.createdAt.seconds * 1000
                        ).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(user)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Display Name"
              value={userData.displayName}
              onChange={(e) =>
                setUserData({ ...userData, displayName: e.target.value })
              }
              required
            />
            <TextField
              fullWidth
              label="Email"
              value={userData.email}
              onChange={(e) =>
                setUserData({ ...userData, email: e.target.value })
              }
              required
            />

            <TextField
              fullWidth
              label="Phone Number"
              value={userData.phoneNumber}
              onChange={(e) =>
                setUserData({ ...userData, phoneNumber: e.target.value })
              }
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={userData.role}
                onChange={(e) =>
                  setUserData({ ...userData, role: e.target.value })
                }
                label="Role"
              >
                <MenuItem value="customer">Customer</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                {canManageFacilityScopedRoles && <MenuItem value="seller">Seller</MenuItem>}
                {canManageFacilityScopedRoles && <MenuItem value="Technician">Technician</MenuItem>}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {editingUser ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserManagement;